<template>
  <div>
    <div class="actions row col-lg-12 mb-5 p-0 m-0 justify-content-between">
      <div class="actions-left">
        <div class="btn-group" role="group">
          <button 
            type="button" 
            class="btn" 
            :class="viewMode === 'list' ? 'btn-primary' : 'btn-outline-primary'"
            @click="viewMode = 'list'"
          >
            <i class="fas fa-list"></i> {{ $t('list') }}
          </button>
          <button 
            type="button" 
            class="btn" 
            :class="viewMode === 'calendar' ? 'btn-primary' : 'btn-outline-primary'"
            @click="viewMode = 'calendar'"
          >
            <i class="fas fa-calendar"></i> {{ $t('calendar') }}
          </button>
        </div>
      </div>
      <div class="actions-right">
        <div class="btn-group" role="group">
          <button type="button" class="btn btn-primary" @click="addReservation">
            <i class="fas fa-plus"></i> {{ $t('reservations.add') }}
          </button>
          <select class="form-control" v-model="statusFilter" @change="updateItemsRequested(1, itemsPerPage)">
            <option value="">{{ $t('reservations.allStatuses') }}</option>
            <option value="Pending">{{ $t('reservations.status.pending') }}</option>
            <option value="Confirmed">{{ $t('reservations.status.confirmed') }}</option>
            <option value="Cancelled">{{ $t('reservations.status.cancelled') }}</option>
            <option value="Completed">{{ $t('reservations.status.completed') }}</option>
          </select>
        </div>
      </div>
    </div>
    
    <!-- List View -->
    <div v-if="viewMode === 'list'">
      <SearchBar @search-clicked="searchClicked"></SearchBar>
      <CTableWrapper 
        ref="table" 
        hover 
        striped 
        :caption="$t('reservations')" 
        :items="items" 
        :actions="actions" 
        :pages="pages" 
        :fields="fields" 
        clickable-rows 
        @row-clicked="rowClicked" 
        @action-clicked="actionClicked" 
        @update-requested="updateItemsRequested" 
        :sorter="{external: true}" 
        :sorterValue="sorterValue" 
        @sorter-value-changed="sorterValueChanged" 
        :loading="loading"
      >
      </CTableWrapper>
    </div>
    
    <!-- Calendar View -->
    <div v-else-if="viewMode === 'calendar'" class="calendar-view">
      <div class="calendar-header">
        <h4>{{ $t('reservations.calendar') }}</h4>
        <div class="calendar-controls">
          <button @click="previousMonth" class="btn btn-outline-secondary btn-sm">
            <i class="fas fa-chevron-left"></i>
          </button>
          <span class="current-month">{{ currentMonthYear }}</span>
          <button @click="nextMonth" class="btn btn-outline-secondary btn-sm">
            <i class="fas fa-chevron-right"></i>
          </button>
          <button @click="resetCalendar" class="btn btn-outline-info btn-sm ml-2" title="Reset to current month">
            <i class="fas fa-home"></i>
          </button>
        </div>
      </div>
      <div class="calendar-grid">
        <div v-for="day in calendarDays" :key="day.date" class="calendar-day" :class="{ 'other-month': !day.currentMonth, 'today': day.isToday }">
          <div class="day-number">{{ day.day }}</div>
          <div class="day-reservations">
            <div v-for="reservation in day.reservations" :key="reservation.id" class="reservation-item" :class="`status-${reservation.status.toLowerCase()}`" @click="viewReservation(reservation)">
              <span class="reservation-time">{{ reservation.time }}</span>
              <span class="reservation-experience">{{ reservation.experience }}</span>
              <span class="reservation-people">{{ reservation.people }} {{ $t('reservations.people') }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import CTableWrapper from "@/controls/PaginatedTable.vue";
import SearchBar from "@/controls/SearchBar.vue";
import router from '@/router';
import InputGetList from "@/classes/InputGetList";
import i18n from '@/helpers/i18n';
import { convertToPascalCase } from "@/helpers/utils.js";

export default {
  components: { CTableWrapper, SearchBar },
  data () {
    return {
      viewMode: 'list', // 'list' or 'calendar'
      searchText: '',
      statusFilter: '',
      itemsPerPage: localStorage.itemsPerPage ?? 10,
      currentDate: (() => {
        const today = new Date();
        const date = new Date(today.getFullYear(), today.getMonth(), 1);
        date.setHours(0, 0, 0, 0);
        return date;
      })(),
      fields: [
        { key: 'touristName', label: i18n.t('reservations.tourist'), _classes: 'font-weight-bold', sortable: true },
        { key: 'experienceName', label: i18n.t('reservations.experience'), sortable: true },
        { key: 'reservationDate', label: i18n.t('reservations.date'), sortable: true },
        { key: 'time', label: i18n.t('reservations.time'), sortable: true },
        { key: 'totalPeople', label: i18n.t('reservations.people'), sortable: true },
        { key: 'status', label: i18n.t('reservations.status'), sortable: true },
        { key: 'paymentMethod', label: i18n.t('reservations.payment'), sortable: true },
      ],
      actions: [
        { key: 'view', label: i18n.t('view') }
      ],
      sorterValue: { column: 'id', asc: false },
      loading: false
    }
  },
  computed: {
    ...mapState('reservations', ['getReservationsOutput']),
    ...mapState('experiences', ['getExperiencesOutput']),
    ...mapState('reservations', ['deleteReservationByIdOutput']),
    reservationsInfo: function() {
      return this.getReservationsOutput ? this.getReservationsOutput : null;
    },
    experienceIdToName() {
      const list = (this.getExperiencesOutput && this.getExperiencesOutput.data) ? this.getExperiencesOutput.data : [];
      const map = new Map();
      list.forEach(e => {
        if (e && e.id != null) {
          map.set(e.id, e.name || '');
        }
      });
      return map;
    },
    items: function()
    {
      const raw = this.reservationsInfo && this.reservationsInfo.data ? this.reservationsInfo.data : [];
      return raw.map(r => {
        const availability = r.experienceAvailability || null;
        const dateStr = (availability && availability.availableDate) || r.reservationDate || null;
        const start = availability && availability.startTime ? availability.startTime.toString().substring(0,5) : '';
        let experienceName = (r.experience && r.experience.name) ? r.experience.name : '';
        if (!experienceName && r.experienceId) {
          experienceName = this.experienceIdToName.get(r.experienceId) || `#${r.experienceId}`;
        }
        // Count people from multiple possible sources
        let totalPeople = 0;
        if (r.totalPeople && typeof r.totalPeople === 'number') {
          totalPeople = r.totalPeople;
        } else if (r.numberOfPeople && typeof r.numberOfPeople === 'number') {
          totalPeople = r.numberOfPeople;
        } else if (Array.isArray(r.people)) {
          totalPeople = r.people.length;
        } else if (r.reservationPeople && Array.isArray(r.reservationPeople)) {
          totalPeople = r.reservationPeople.length;
        } else if (r.people && typeof r.people === 'number') {
          totalPeople = r.people;
        }
        
        return {
          id: r.id,
          touristName: r.touristName,
          experienceName: experienceName,
          reservationDate: dateStr ? new Date(dateStr).toLocaleDateString() : '',
          time: availability ? start : '',
          totalPeople: totalPeople,
          status: r.status,
          paymentMethod: (r.prePaymentRequired && r.paymentMethod) ? r.paymentMethod : ''
        };
      });
    },
    pages: function()
    {
      return this.reservationsInfo ? this.reservationsInfo.totalPages : 1;
    },
    currentMonthYear() {
      return this.currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    },
    calendarDays() {
      try {
        const year = this.currentDate.getFullYear();
        const month = this.currentDate.getMonth();
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // Verificar se a data atual é válida
        if (isNaN(this.currentDate.getTime())) {
          console.error('Invalid currentDate:', this.currentDate);
          return [];
        }
        
        const firstDay = new Date(year, month, 1);
        const startDate = new Date(year, month, 1 - firstDay.getDay());
        
        const days = [];
        const seenDates = new Set(); // Para evitar duplicatas
        const current = new Date(startDate);
        
        // Gerar exatamente 42 dias (6 semanas)
        for (let i = 0; i < 42; i++) {
          // Verificar se a data é válida antes de processar
          if (isNaN(current.getTime())) {
            console.error('Invalid date in calendar loop:', current);
            break;
          }
          
          // Formatar data localmente sem problemas de timezone
          const dateStr = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}-${String(current.getDate()).padStart(2, '0')}`;
          
          // Verificar se já existe essa data (evitar duplicatas)
          if (seenDates.has(dateStr)) {
            console.warn('Duplicate date detected:', dateStr);
            current.setDate(current.getDate() + 1);
            continue;
          }
          
          seenDates.add(dateStr);
          
          const isCurrentMonth = current.getMonth() === month;
          const currentDateOnly = new Date(current);
          currentDateOnly.setHours(0, 0, 0, 0);
          const isToday = currentDateOnly.getTime() === today.getTime();
          
          days.push({
            date: dateStr,
            day: current.getDate(),
            currentMonth: isCurrentMonth,
            isToday: isToday,
            reservations: this.getReservationsForDate(current)
          });
          
          // Avançar para o próximo dia de forma segura
          const nextDate = new Date(current);
          nextDate.setDate(nextDate.getDate() + 1);
          current.setTime(nextDate.getTime());
        }
        
        return days;
      } catch (error) {
        console.error('Error in calendarDays:', error);
        return [];
      }
    }
  },
  mounted: async function()
  {
    // Load experiences first to ensure names are available
    try {
      // Pass Position as propertyName since experiences are ordered by Position anyway
      await this.getExperiences({ 
        pageSize: 100, 
        pageNumber: 1,
        propertyName: 'Position',
        orderBy: 1
      });
    } catch (error) {
      console.error('Error loading experiences on mount:', error);
      // Continue even if experiences fail - reservations will show IDs
    }
    
    this.updateItemsRequested(1, this.itemsPerPage);
  },
  methods: {
    ...mapActions('reservations', ['getReservations']),
    ...mapActions('experiences', ['getExperiences']),
    ...mapActions('reservations', ['deleteReservationById']),
    rowClicked (item, index) {
      this.$router.push({
        name: "ReservationsEdit", 
        query: { id: item.id }, 
        params : { 
          name: item.touristName,
          time: item.time,
          totalPeople: item.totalPeople
        } 
      });
    },
    actionClicked(item, action){
      switch(action.key)
      {
        case 'view':
        {
          this.$router.push({
            name: "ReservationsEdit", 
            query: { id: item.id }, 
            params : { 
              name: item.touristName,
              time: item.time,
              totalPeople: item.totalPeople
            } 
          });
          break;
        }
        default:
        {
          throw "The provided action is not supported!"; 
        }
      }
    },
    updateItemsRequested(currentPage, itemsPerPage)
    {
      this.loading = true;
      this.itemsPerPage = itemsPerPage;

      var input = new InputGetList(itemsPerPage, currentPage, this.searchText, this.sorterValue.asc ? 1 : 2, convertToPascalCase(this.sorterValue.column || ''));
      input.status = this.statusFilter;

      // Load experiences once for name lookup - ensure it's loaded before getting reservations
      if (!this.getExperiencesOutput || !this.getExperiencesOutput.data || this.getExperiencesOutput.data.length === 0) {
        // Pass Position as propertyName since experiences are ordered by Position anyway
        this.getExperiences({ 
          pageSize: 100, 
          pageNumber: 1,
          propertyName: 'Position',
          orderBy: 1
        }).catch(err => {
          console.error('Error loading experiences:', err);
          // Continue even if experiences fail to load - reservations will still show IDs
        });
      }

      this.getReservations(input); 
    },
    viewReservation(reservation) {
      this.$router.push({
        name: "ReservationsEdit", 
        query: { id: reservation.id }, 
        params : { 
          name: reservation.touristName,
          time: reservation.time,
          totalPeople: reservation.people
        } 
      });
    },
    addReservation() {
      this.$router.push({name: "ReservationsAdd"});
    },
    searchClicked(searchText)
    {
      this.searchText = searchText;
      this.$refs.table.reset();

      this.updateItemsRequested(1, this.itemsPerPage);
    },
    sorterValueChanged(value)
    {
      this.sorterValue = value;
    },
    previousMonth() {
      try {
        const year = this.currentDate.getFullYear();
        const month = this.currentDate.getMonth();
        
        // Criar nova data de forma segura
        const newDate = new Date(year, month - 1, 1);
        newDate.setHours(0, 0, 0, 0);
        
        // Verificar se a data é válida
        if (isNaN(newDate.getTime())) {
          console.error('Invalid date created:', { year, month: month - 1 });
          return;
        }
        
        // Atualizar usando Vue.set para garantir reatividade
        this.$set(this, 'currentDate', newDate);
        
      } catch (error) {
        console.error('Error in previousMonth:', error);
      }
    },
    nextMonth() {
      try {
        const year = this.currentDate.getFullYear();
        const month = this.currentDate.getMonth();
        
        // Criar nova data de forma segura
        const newDate = new Date(year, month + 1, 1);
        newDate.setHours(0, 0, 0, 0);
        
        // Verificar se a data é válida
        if (isNaN(newDate.getTime())) {
          console.error('Invalid date created:', { year, month: month + 1 });
          return;
        }
        
        // Atualizar usando Vue.set para garantir reatividade
        this.$set(this, 'currentDate', newDate);
        
      } catch (error) {
        console.error('Error in nextMonth:', error);
      }
    },
    getReservationsForDate(date) {
      try {
        // Verificar se a data é válida
        if (!date || isNaN(date.getTime())) {
          console.error('Invalid date in getReservationsForDate:', date);
          return [];
        }
        
        // Formatar data localmente sem problemas de timezone
        const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        
        const raw = this.reservationsInfo && this.reservationsInfo.data ? this.reservationsInfo.data : [];
        
        return raw
          .filter(r => {
            try {
              const reservationDate = (r.experienceAvailability && r.experienceAvailability.availableDate) || r.reservationDate;
              if (!reservationDate) return false;
              
              // Extrair apenas a parte da data (YYYY-MM-DD) da string
              const reservationDateStr = reservationDate.split('T')[0];
              return reservationDateStr === dateStr;
            } catch (error) {
              console.error('Error filtering reservation:', error, r);
              return false;
            }
          })
          .map(r => {
            try {
              const availability = r.experienceAvailability || null;
              const start = availability && availability.startTime ? availability.startTime.toString().substring(0,5) : '';
              let experienceName = (r.experience && r.experience.name) ? r.experience.name : '';
              if (!experienceName && r.experienceId) {
                experienceName = this.experienceIdToName.get(r.experienceId) || `#${r.experienceId}`;
              }
              
              // Count people from multiple possible sources
              let people = 0;
              if (r.totalPeople && typeof r.totalPeople === 'number') {
                people = r.totalPeople;
              } else if (r.numberOfPeople && typeof r.numberOfPeople === 'number') {
                people = r.numberOfPeople;
              } else if (Array.isArray(r.people)) {
                people = r.people.length;
              } else if (r.reservationPeople && Array.isArray(r.reservationPeople)) {
                people = r.reservationPeople.length;
              } else if (r.people && typeof r.people === 'number') {
                people = r.people;
              }
              
              return {
                id: r.id,
                time: availability ? start : '',
                experience: experienceName,
                people: people,
                status: r.status,
                touristName: r.touristName
              };
            } catch (error) {
              console.error('Error mapping reservation:', error, r);
              return null;
            }
          })
          .filter(r => r !== null); // Remove nulls
      } catch (error) {
        console.error('Error in getReservationsForDate:', error);
        return [];
      }
    },
    resetCalendar() {
      try {
        const today = new Date();
        const newDate = new Date(today.getFullYear(), today.getMonth(), 1);
        newDate.setHours(0, 0, 0, 0);
        
        // Atualizar usando Vue.set para garantir reatividade
        this.$set(this, 'currentDate', newDate);
      } catch (error) {
        console.error('Error resetting calendar:', error);
      }
    }
  },
  watch:
  {
    sorterValue:
    {
      handler: function(val)
      {
        this.updateItemsRequested(1, this.itemsPerPage);
      },
      deep: true
    },
    deleteReservationByIdOutput: function(val)
    {
      this.loading = true;

      if(val)
      {
        this.updateItemsRequested(1, this.itemsPerPage);
      }
    },
    getReservationsOutput: function(val)
    {
      if(val != null)
      {
        this.loading = false;
      }
    }
  }
}
</script>

<style scoped>
.actions-left {
  display: flex;
  align-items: center;
}

.btn-group .btn {
  margin-right: 0;
}

.calendar-view {
  background: white;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #dee2e6;
}

.calendar-controls {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.current-month {
  min-width: 180px;
  text-align: center;
  font-weight: bold;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 1px;
  background: #dee2e6;
  border-radius: 4px;
  overflow: hidden;
}

.calendar-day {
  background: white;
  min-height: 120px;
  padding: 0.5rem;
  position: relative;
}

.calendar-day.other-month {
  background: #f8f9fa;
  color: #6c757d;
}

.calendar-day.today {
  background: #e3f2fd;
}

.day-number {
  font-weight: bold;
  margin-bottom: 0.5rem;
}

.day-reservations {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.reservation-item {
  padding: 2px 4px;
  border-radius: 3px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: background-color 0.2s;
  color: white;
}

.reservation-item.status-pending {
  background: #ffc107;
}

.reservation-item.status-confirmed {
  background: #28a745;
}

.reservation-item.status-cancelled {
  background: #dc3545;
}

.reservation-item.status-completed {
  background: #6c757d;
}


.reservation-item:hover {
  opacity: 0.8;
}

.reservation-time {
  display: block;
  font-weight: bold;
}

.reservation-experience {
  display: block;
  font-size: 0.7rem;
  opacity: 0.9;
}

.reservation-people {
  display: block;
  font-size: 0.7rem;
  opacity: 0.9;
}

@media (max-width: 768px) {
  .calendar-grid {
    font-size: 0.8rem;
  }
  
  .calendar-day {
    min-height: 80px;
    padding: 0.25rem;
  }
  
  .reservation-item {
    font-size: 0.7rem;
  }
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1050;
}

.modal-content {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
}

.close:hover {
  color: #000;
}

.modal-body {
  padding: 1.5rem;
}

.modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #dee2e6;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}
</style>

